import React, { useEffect } from "react";
import {
  CheckCircle,
  Phone,
  Calendar,
  Instagram,
  Facebook,
  Youtube,
  Linkedin,
} from "lucide-react";

const ThankYouPage: React.FC = () => {
  // Add useEffect to clear localStorage when component mounts
  React.useEffect(() => {
    localStorage.clear();
  }, []);

  // Prevent going back - comprehensive approach
  useEffect(() => {
    // Disable browser back button
    const preventBack = (e: PopStateEvent) => {
      e.preventDefault();
      window.history.pushState(null, "", window.location.href);
      alert("You cannot go back from this page. The test is completed.");
    };

    // Clear browser history and prevent back navigation
    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", preventBack);

    // Prevent navigation away from the page
    const preventLeave = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };

    // Prevent form submission and other navigation
    const preventNavigation = (e: Event) => {
      e.preventDefault();
      alert("You cannot navigate away from this page. The test is completed.");
    };

    // Add event listeners
    window.addEventListener("beforeunload", preventLeave);
    document.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      const href = target.getAttribute("href");
      if (
        target.tagName === "A" &&
        href &&
        !href.startsWith("http") &&
        !href.startsWith("mailto:") &&
        !href.startsWith("tel:")
      ) {
        preventNavigation(e);
      }
    });

    // Scroll to top when component mounts
    window.scrollTo(0, 0);

    // Cleanup function
    return () => {
      window.removeEventListener("popstate", preventBack);
      window.removeEventListener("beforeunload", preventLeave);
    };
  }, []);

  const socialLinks = [
    {
      icon: <Instagram size={24} />,
      url: "https://www.instagram.com/navishumanresources/",
      color: "hover:text-pink-500",
    },
    {
      icon: <Facebook size={24} />,
      url: "https://www.facebook.com/NAVISHROFFICIAL/",
      color: "hover:text-blue-600",
    },
    {
      icon: <Youtube size={24} />,
      url: "https://www.youtube.com/@navishumanresources",
      color: "hover:text-red-600",
    },
    {
      icon: <Linkedin size={24} />,
      url: "https://www.linkedin.com/company/navishumanresources/",
      color: "hover:text-blue-700",
    },
  ];

  return (
    <div className="bg-gradient-to-br from-red-50 to-red-200 min-h-screen flex items-center justify-center p-4">
      <div className="max-w-xl w-full bg-white rounded-xl shadow-xl overflow-hidden transform transition-all">
        {/* Header with animated background */}
        <div className="bg-gradient-to-r from-red-500 to-red-600 p-8 text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full opacity-10">
            <div className="absolute -top-8 -left-8 w-32 h-32 bg-white rounded-full"></div>
            <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-white rounded-full"></div>
          </div>
          <div className="inline-flex justify-center items-center w-20 h-20 rounded-full bg-white mb-4 shadow-md relative z-10">
            <CheckCircle className="text-red-600 w-12 h-12" />
          </div>
          <h1 className="text-3xl font-bold text-white relative z-10">
            🎉 Test Completed! 🎉
          </h1>
          <p className="text-red-100 mt-2 relative z-10">
            Congratulations on completing your assessment
          </p>
        </div>

        <div className="p-8">
          {/* Test info section */}
          <div className="mb-6 bg-gradient-to-r from-yellow-50 to-yellow-100 p-5 rounded-lg border-l-4 border-yellow-400 shadow-sm">
            <div className="flex items-center">
              <Calendar className="text-yellow-600 w-6 h-6 mr-3" />
              <h3 className="font-semibold text-yellow-800">
                Japanese Language Audio Test
              </h3>
            </div>
          </div>

          {/* Contact section */}
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-5 rounded-lg border-l-4 border-blue-400 mb-6 shadow-sm">
            <h3 className="font-semibold text-blue-800 mb-3 flex items-center">
              <Phone className="w-6 h-6 mr-3" />
              For any queries, contact us at:
            </h3>
            <ul className="space-y-3 pl-9">
              <li className="text-gray-700 flex items-center">
                <span className="inline-block w-3 h-3 bg-blue-500 rounded-full mr-3"></span>
                <span className="font-medium">+91 9071002350</span>{" "}
                <span className="text-gray-500 ml-2">(Nandidni San)</span>
              </li>
              <li className="text-gray-700 flex items-center">
                <span className="inline-block w-3 h-3 bg-blue-500 rounded-full mr-3"></span>
                <span className="font-medium">+91 9071002349</span>{" "}
                <span className="text-gray-500 ml-2">(Swaroop San)</span>
              </li>
              <li className="text-gray-700 flex items-center">
                <span className="inline-block w-3 h-3 bg-blue-500 rounded-full mr-3"></span>
                <span className="font-medium">+91 9071001353</span>{" "}
                <span className="text-gray-500 ml-2">(Harshitha San)</span>
              </li>
            </ul>
          </div>

          {/* Social media section */}
          <div className="bg-gradient-to-r from-red-50 to-pink-50 p-5 rounded-lg shadow-sm border border-red-100">
            <h3 className="font-semibold text-red-800 mb-4 text-center">
              Connect With Us
            </h3>
            <div className="flex justify-center space-x-6">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`p-3 bg-white rounded-full shadow-sm text-gray-600 transition-all duration-300 ${social.color} hover:shadow-md transform hover:-translate-y-1`}
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-gray-50 to-red-50 p-4 text-center border-t border-red-100">
          <p className="text-gray-600">
            Thank you for participating in our assessment process.
          </p>
          <a
            href="http://navishr.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-red-600 hover:text-red-700 font-medium mt-2 inline-block transition-colors duration-300"
          >
            Visit our website
          </a>
          <p className="text-xs text-gray-500 mt-3">
            © NAVIS HR 2025. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ThankYouPage;
